import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  peerReviewQueue,
  decidePeerReview,
  myPeerReviewStats,
  myPeerReviewDecisions,
  updatePeerReviewDecision,
  fetchRecitationBlobUrl,
  fetchPeerReviewVoiceBlobUrl,
  uploadPeerReviewVoice,
} from "@/lib/apiClient";
import { pickMime } from "@/lib/audioRecording";
import {
  Star, Award, Send, Loader2, RefreshCw, Sparkles,
  Mic, Square, AlertTriangle, Trash2, CheckCircle2, Play, Pencil, X,
  ChevronLeft, ChevronRight,
} from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import { checkAchievements } from "@/lib/achievements";

/**
 * /peer-review — community peer-review queue (SRS V2.0 Layer 2).
 * Reviewers rate four criteria (Fluency / Accuracy / Confidence / Continuity),
 * write an optional comment, and may attach a ≤60s voice note.
 */
const RUBRIC_KEYS = ["fluency", "accuracy", "confidence", "continuity"];
const VOICE_MAX_MS = 60 * 1000;

const EMPTY_RUBRIC = {
  fluency: 0, accuracy: 0, confidence: 0, continuity: 0, comment: "",
};

/**
 * Reusable Peer Assessment panel — reviewer queue + rubric form + review
 * history. Embedded inside the Certificate hub's Peer Assessment tab (no
 * Header/Footer chrome of its own so it composes into any page shell).
 */
export function PeerReviewPanel() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);
  const [decisions, setDecisions] = useState([]);
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rubric, setRubric] = useState(EMPTY_RUBRIC);
  // Audio playback for the current submission — fetched with auth header
  // and exposed as a blob URL because <audio> can't send the Bearer token.
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioError, setAudioError] = useState(null);

  // Voice-feedback recorder state.
  const [voiceState, setVoiceState] = useState("idle"); // idle | recording | recorded | uploading
  const [voiceBlob, setVoiceBlob] = useState(null);
  const [voiceUrl, setVoiceUrl] = useState(null);
  const [voiceMs, setVoiceMs] = useState(0);
  const [voiceError, setVoiceError] = useState(null);
  const [voiceFeedbackId, setVoiceFeedbackId] = useState(null);
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const recStartRef = useRef(null);
  const recTickerRef = useRef(null);

  // Reviewed IDs persisted to localStorage so they survive page navigation.
  // Key holds up to 200 IDs; oldest are dropped when the cap is hit.
  const REVIEWED_KEY = "qf:pr:reviewed-ids";
  const reviewedIdsRef = useRef(null);
  if (reviewedIdsRef.current === null) {
    try {
      const stored = JSON.parse(localStorage.getItem(REVIEWED_KEY) || "[]");
      reviewedIdsRef.current = new Set(Array.isArray(stored) ? stored : []);
    } catch {
      reviewedIdsRef.current = new Set();
    }
  }

  const markReviewed = (id) => {
    reviewedIdsRef.current.add(id);
    try {
      const arr = Array.from(reviewedIdsRef.current).slice(-200);
      localStorage.setItem(REVIEWED_KEY, JSON.stringify(arr));
    } catch { /* storage full — not critical */ }
  };

  const load = async () => {
    try {
      const [q, s, d] = await Promise.all([
        peerReviewQueue(50),
        myPeerReviewStats(),
        myPeerReviewDecisions(),
      ]);
      setItems(q?.items || []);
      setIdx(0);
      setStats(s || null);
      setDecisions(d?.decisions || []);
    } catch (_e) { /* not blocking */ }
  };

  useEffect(() => { load(); }, []);
  // Cleanup voice resources on unmount.
  useEffect(() => () => {
    if (voiceUrl) URL.revokeObjectURL(voiceUrl);
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    if (recTickerRef.current) clearInterval(recTickerRef.current);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const current = items[idx] || null;
  const alreadyReviewed = current ? reviewedIdsRef.current.has(current.submission_id) : false;

  // Fetch the recitation audio with the Bearer token for the currently
  // pinned submission — re-runs on Previous/Next too, so a half-filled
  // rubric/voice note for one submission never bleeds into another.
  // <audio src=...> can't send headers cross-origin so we resolve the
  // asset to a blob URL up-front.
  useEffect(() => {
    let cancelled = false;
    let createdUrl = null;
    setAudioError(null);
    setAudioUrl(null);
    setRubric(EMPTY_RUBRIC);
    resetVoice();
    if (!current?.recitation_id) return undefined;
    (async () => {
      try {
        const url = await fetchRecitationBlobUrl(current.recitation_id);
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        createdUrl = url;
        setAudioUrl(url);
      } catch (e) {
        if (!cancelled) setAudioError(e?.response?.data?.detail || t("pr.audio_load_failed"));
      }
    })();
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [current?.recitation_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const setScore = (k, v) => setRubric((r) => ({ ...r, [k]: v }));
  const ready = RUBRIC_KEYS.every((k) => rubric[k] > 0);

  const resetVoice = () => {
    if (voiceUrl) URL.revokeObjectURL(voiceUrl);
    setVoiceBlob(null); setVoiceUrl(null); setVoiceMs(0);
    setVoiceFeedbackId(null); setVoiceError(null); setVoiceState("idle");
  };

  const startVoice = async () => {
    setVoiceError(null);
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      setVoiceError(t("pr.voice_err_unsupported"));
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = pickMime();
      const rec = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      rec.onstop = () => {
        stream.getTracks().forEach((tr) => tr.stop());
        if (!chunksRef.current.length) {
          setVoiceError(t("pr.voice_err_empty")); setVoiceState("idle");
          return;
        }
        const blobType = rec.mimeType || mime || "audio/webm";
        const b = new Blob(chunksRef.current, { type: blobType });
        if (b.size < 256) {
          setVoiceError(t("pr.voice_err_empty")); setVoiceState("idle");
          return;
        }
        setVoiceBlob(b);
        setVoiceUrl(URL.createObjectURL(b));
        setVoiceState("recorded");
      };
      recorderRef.current = rec;
      recStartRef.current = Date.now();
      rec.start();
      setVoiceState("recording");
      recTickerRef.current = setInterval(() => {
        const ms = Date.now() - recStartRef.current;
        setVoiceMs(ms);
        if (ms >= VOICE_MAX_MS) {
          // Auto-stop at the 60s cap.
          stopVoice();
        }
      }, 100);
    } catch (e) {
      const name = e?.name || "";
      if (name === "NotAllowedError" || name === "SecurityError") {
        setVoiceError(t("pr.voice_err_blocked"));
      } else if (name === "NotFoundError" || name === "DevicesNotFoundError") {
        setVoiceError(t("pr.voice_err_no_mic"));
      } else {
        setVoiceError(t("pr.voice_err_generic"));
      }
    }
  };

  const stopVoice = () => {
    if (recTickerRef.current) { clearInterval(recTickerRef.current); recTickerRef.current = null; }
    const rec = recorderRef.current;
    if (rec && rec.state !== "inactive") {
      try { rec.stop(); } catch (_e) { /* harmless */ }
    }
  };

  const submit = async () => {
    if (!current || !ready || alreadyReviewed) return;
    setBusy(true);
    try {
      // If a fresh voice clip exists, upload it first to obtain an id.
      let voiceId = voiceFeedbackId;
      if (voiceBlob && !voiceId) {
        setVoiceState("uploading");
        const up = await uploadPeerReviewVoice(voiceBlob, "peer-feedback.webm");
        voiceId = up.voice_feedback_id;
        setVoiceFeedbackId(voiceId);
      }
      const submittedId = current.submission_id;
      const res = await decidePeerReview({
        submission_id: submittedId,
        rubric: {
          fluency: rubric.fluency,
          accuracy: rubric.accuracy,
          confidence: rubric.confidence,
          continuity: rubric.continuity,
          comment: rubric.comment || null,
        },
        voice_feedback_id: voiceId || null,
        flagged_words: null,
      });
      toast.success(t("pr.toast_thanks", { n: res.submission_reviews }));
      setRubric(EMPTY_RUBRIC);
      resetVoice();
      // Persist as reviewed so load() never re-adds it, even across
      // page navigations (localStorage-backed).
      markReviewed(submittedId);
      setItems((prev) => prev.filter((i) => i.submission_id !== submittedId));
      checkAchievements();
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      toast.error(detail || t("pr.toast_failed"));
      // If the server says we already reviewed this, mark it locally so
      // load() will filter it out and the card won't get stuck.
      if (detail === "Already reviewed" && current) {
        markReviewed(current.submission_id);
      }
      await load();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-3xl" data-testid="peer-review-panel">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-[#D97706]">{t("pr.eyebrow")}</div>
            <h1 className="font-heading text-3xl sm:text-4xl font-semibold mt-2 text-[#1F2937]">{t("pr.title")}</h1>
            <p className="text-[#4B5563] mt-3 max-w-xl" dangerouslySetInnerHTML={{ __html: t("pr.intro_html") }} />
          </div>
          {stats && (
            <div className="rounded-2xl bg-white border border-[#E7E0D2] px-4 py-3 flex items-center gap-3" data-testid="peer-review-stats">
              <Award className="w-5 h-5 text-[#D97706]" />
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[#4B5563]">{t("pr.your_points")}</div>
                <div className="font-heading text-xl text-[#1F2937]">{stats.peer_review_points || 0}</div>
              </div>
            </div>
          )}
        </div>

        <div className="mt-8">
          {!current ? (
            <EmptyQueue onRefresh={load} t={t} decisions={decisions} />
          ) : (
            <>
              {items.length > 1 && (
                <div className="flex items-center justify-between gap-2 mb-3" data-testid="peer-review-nav">
                  <button
                    data-testid="peer-review-prev"
                    onClick={() => setIdx((i) => Math.max(0, i - 1))}
                    disabled={idx === 0}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white border border-[#E7E0D2] hover:border-[#064E3B] text-sm text-[#1F2937] disabled:opacity-40 disabled:cursor-not-allowed transition">
                    <ChevronLeft className="w-4 h-4" /> Previous
                  </button>
                  <span className="text-xs text-[#4B5563] tabular-nums">{idx + 1} of {items.length}</span>
                  <button
                    data-testid="peer-review-next"
                    onClick={() => setIdx((i) => Math.min(items.length - 1, i + 1))}
                    disabled={idx === items.length - 1}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white border border-[#E7E0D2] hover:border-[#064E3B] text-sm text-[#1F2937] disabled:opacity-40 disabled:cursor-not-allowed transition">
                    Next <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            <div className="rounded-2xl bg-white border border-[#E7E0D2] p-6" data-testid="peer-review-card">
              {/* Submission header — always visible. Module and section are
                  shown as two distinct pieces so reviewers know exactly
                  which part of which module they're reviewing, not just a
                  single ambiguous code. */}
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs uppercase tracking-[0.18em] text-[#4B5563]">{t("pr.module_label", { code: current.module_name || current.module_code || "—" })}</span>
                {current.section && (
                  <span className="inline-flex items-center gap-1 text-[11px] font-medium text-[#064E3B] bg-[#ECFDF5] border border-[#A7F3D0] rounded-full px-2 py-0.5">
                    Section {current.section}
                  </span>
                )}
              </div>
              <div className="mt-1 flex items-center gap-2 flex-wrap">
                <span className="font-heading text-lg text-[#1F2937]">{t("pr.submission_no", { id: current.submission_id.slice(-6) })}</span>
                {current.submitter_name && (
                  <span className="text-sm text-[#4B5563]">by {current.submitter_name}</span>
                )}
                {alreadyReviewed && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-[#ECFDF5] text-[#064E3B] text-[11px] font-semibold border border-[#A7F3D0]">
                    <CheckCircle2 className="w-3 h-3" /> Already reviewed
                  </span>
                )}
              </div>
              {current.note && (
                <div className="mt-2 text-sm text-[#4B5563] italic">&ldquo;{current.note}&rdquo;</div>
              )}
              <audio
                data-testid="peer-review-audio"
                src={audioUrl || undefined}
                controls className="w-full mt-5" />
              {!audioUrl && !audioError && (
                <div data-testid="peer-review-audio-loading" className="mt-2 text-xs text-[#4B5563]">
                  <Loader2 className="w-3 h-3 inline animate-spin mr-1" /> {t("pr.audio_loading")}
                </div>
              )}
              {audioError && (
                <div data-testid="peer-review-audio-error" className="mt-2 flex items-start gap-2 text-xs text-[#B45309] bg-[#FFF7ED] border border-[#FED7AA] rounded-xl px-3 py-2">
                  <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                  <span>{audioError}</span>
                </div>
              )}
              {current.is_for_me && !alreadyReviewed && (
                <div data-testid="peer-review-friend-tag" className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#ECFDF5] text-[#064E3B] text-[11px] font-semibold border border-[#A7F3D0]">
                  {t("pr.friend_pinned")}
                </div>
              )}

              {alreadyReviewed ? (
                /* ---- Already-reviewed state ---- */
                <div className="mt-6">
                  <div className="rounded-2xl bg-[#ECFDF5] border border-[#A7F3D0] px-5 py-4 flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#064E3B] mt-0.5 shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-[#064E3B]">Review submitted</p>
                      <p className="text-xs text-[#4B5563] mt-0.5">You've already reviewed this recording. Move to the next one.</p>
                    </div>
                  </div>
                  <div className="mt-4 flex items-center gap-3">
                    <button
                      onClick={() => {
                        setItems((prev) => prev.slice(1));
                        setRubric(EMPTY_RUBRIC);
                        resetVoice();
                        load();
                      }}
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#064E3B] hover:bg-[#047857] text-white font-medium text-sm transition">
                      <RefreshCw className="w-4 h-4" /> Next recording
                    </button>
                  </div>
                </div>
              ) : (
                /* ---- Normal review form ---- */
                <>
                  <div className="mt-6 space-y-4">
                    <RubricRow label={t("pr.rubric_fluency")} hint={t("pr.rubric_fluency_hint")} value={rubric.fluency} onSet={(v) => setScore("fluency", v)} testid="rubric-fluency" />
                    <RubricRow label={t("pr.rubric_accuracy")} hint={t("pr.rubric_accuracy_hint")} value={rubric.accuracy} onSet={(v) => setScore("accuracy", v)} testid="rubric-accuracy" />
                    <RubricRow label={t("pr.rubric_confidence")} hint={t("pr.rubric_confidence_hint")} value={rubric.confidence} onSet={(v) => setScore("confidence", v)} testid="rubric-confidence" />
                    <RubricRow label={t("pr.rubric_continuity")} hint={t("pr.rubric_continuity_hint")} value={rubric.continuity} onSet={(v) => setScore("continuity", v)} testid="rubric-continuity" />
                    <div>
                      <div className="text-xs uppercase tracking-[0.16em] text-[#4B5563] mb-1.5">{t("pr.optional_comment")}</div>
                      <textarea
                        data-testid="rubric-comment"
                        value={rubric.comment}
                        onChange={(e) => setRubric({ ...rubric, comment: e.target.value })}
                        rows={3}
                        maxLength={1000}
                        placeholder={t("pr.comment_placeholder")}
                        className="w-full px-3 py-2 rounded-lg border border-[#E7E0D2] focus:border-[#064E3B] outline-none text-sm resize-none" />
                    </div>

                    <VoiceFeedback
                      t={t}
                      state={voiceState}
                      ms={voiceMs}
                      url={voiceUrl}
                      error={voiceError}
                      onStart={startVoice}
                      onStop={stopVoice}
                      onReset={resetVoice}
                    />
                  </div>

                  <div className="mt-6 flex items-center gap-3 flex-wrap">
                    <button
                      data-testid="peer-review-submit"
                      onClick={submit} disabled={busy || !ready || voiceState === "recording"}
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#064E3B] hover:bg-[#047857] disabled:opacity-30 disabled:cursor-not-allowed text-white font-medium text-sm transition">
                      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      {t("pr.submit_btn")}
                    </button>
                    <button
                      data-testid="peer-review-skip"
                      onClick={() => { setRubric(EMPTY_RUBRIC); resetVoice(); load(); }}
                      disabled={voiceState === "recording"}
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white border border-[#E7E0D2] hover:border-[#064E3B] disabled:opacity-50 text-[#4B5563] text-sm transition">
                      <RefreshCw className="w-4 h-4" /> {t("pr.skip")}
                    </button>
                  </div>
                </>
              )}
            </div>
            </>
          )}
        </div>
    </div>
  );
}

function RubricRow({ label, hint, value, onSet, testid }) {
  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between mb-1.5">
        <div>
          <div className="text-sm font-medium text-[#1F2937]">{label}</div>
          {hint && <div className="text-[11px] text-[#4B5563] mt-0.5">{hint}</div>}
        </div>
        <div className="text-xs text-[#4B5563]">{value || 0}/5</div>
      </div>
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            data-testid={`${testid}-star-${n}`}
            onClick={() => onSet(n)}
            className={`w-9 h-9 rounded-lg grid place-items-center transition ${
              n <= value ? "bg-[#D97706] text-white" : "bg-white border border-[#E7E0D2] text-[#9CA3AF] hover:border-[#D97706] hover:text-[#D97706]"
            }`}>
            <Star className="w-4 h-4" fill={n <= value ? "currentColor" : "none"} />
          </button>
        ))}
      </div>
    </div>
  );
}

function VoiceFeedback({ t, state, ms, url, error, onStart, onStop, onReset }) {
  const secs = (ms / 1000).toFixed(1);
  const remaining = Math.max(0, 60 - Math.floor(ms / 1000));
  return (
    <div data-testid="voice-feedback" className="rounded-2xl bg-[#FFF7ED] border border-[#FED7AA] p-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-[160px]">
          <div className="text-xs uppercase tracking-[0.16em] text-[#B45309] font-semibold">{t("pr.voice_title")}</div>
          <div className="text-xs text-[#4B5563] mt-1">{t("pr.voice_subtitle")}</div>
        </div>
        <div className="flex items-center gap-2">
          {state === "idle" && (
            <button
              data-testid="voice-start"
              type="button"
              onClick={onStart}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-[#D97706] hover:bg-[#B45309] text-white text-sm font-medium transition">
              <Mic className="w-4 h-4" /> {t("pr.voice_record")}
            </button>
          )}
          {state === "recording" && (
            <button
              data-testid="voice-stop"
              type="button"
              onClick={onStop}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-[#EF4444] hover:bg-[#DC2626] text-white text-sm font-medium transition">
              <Square className="w-4 h-4" /> {t("pr.voice_stop")}
            </button>
          )}
          {state === "recorded" && (
            <button
              data-testid="voice-reset"
              type="button"
              onClick={onReset}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-white border border-[#E7E0D2] hover:border-[#B45309] text-[#4B5563] text-sm transition">
              <Trash2 className="w-4 h-4" /> {t("pr.voice_reset")}
            </button>
          )}
        </div>
      </div>

      {state === "recording" && (
        <div data-testid="voice-recording-status" className="mt-3 flex items-center gap-2 text-sm text-[#1F2937]">
          <span className="w-2 h-2 rounded-full bg-[#EF4444] animate-pulse" />
          <span className="font-medium tabular-nums">{secs}s</span>
          <span className="text-xs text-[#4B5563]">{t("pr.voice_remaining", { s: remaining })}</span>
        </div>
      )}

      {state === "recorded" && url && (
        <div className="mt-3 flex items-center gap-3 flex-wrap">
          <audio data-testid="voice-playback" controls preload="metadata" src={url} className="h-9 max-w-xs" />
          <span className="text-xs text-[#4B5563]">{t("pr.voice_attached", { s: secs })}</span>
        </div>
      )}

      {error && (
        <div data-testid="voice-error" className="mt-3 flex items-start gap-2 text-xs text-[#B45309] bg-white border border-[#FED7AA] rounded-xl px-3 py-2">
          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}

const RUBRIC_LABELS = {
  fluency: "Fluency",
  accuracy: "Accuracy",
  confidence: "Confidence",
  continuity: "Continuity",
};

function VoicePlayback({ voiceFeedbackId }) {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(false);

  const load = async () => {
    if (url || loading) return;
    setLoading(true);
    try {
      const blobUrl = await fetchPeerReviewVoiceBlobUrl(voiceFeedbackId);
      setUrl(blobUrl);
    } catch {
      setErr(true);
    } finally {
      setLoading(false);
    }
  };

  if (err) return (
    <span className="text-[11px] text-[#EF4444]">Failed to load audio</span>
  );

  return url ? (
    <audio controls src={url} className="h-8 max-w-xs" />
  ) : (
    <button
      type="button"
      onClick={load}
      disabled={loading}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#FFF7ED] border border-[#FED7AA] text-[#B45309] text-xs font-medium hover:bg-[#FEF3C7] disabled:opacity-50 transition">
      {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
      {loading ? "Loading…" : "Play voice note"}
    </button>
  );
}

function DecisionCard({ d: initialD, onUpdated }) {
  const { t } = useI18n();
  const [d, setD] = useState(initialD);
  const [editing, setEditing] = useState(false);
  const [editRubric, setEditRubric] = useState({ ...d.rubric });
  const [saving, setSaving] = useState(false);

  const date = d.created_at
    ? new Date(d.created_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })
    : null;

  const openEdit = () => {
    setEditRubric({ ...d.rubric });
    setEditing(true);
  };

  const saveEdit = async () => {
    if (RUBRIC_KEYS.some((k) => !editRubric[k])) return;
    setSaving(true);
    try {
      await updatePeerReviewDecision(d.decision_id, {
        rubric: {
          fluency: editRubric.fluency,
          accuracy: editRubric.accuracy,
          confidence: editRubric.confidence,
          continuity: editRubric.continuity,
          comment: editRubric.comment || null,
        },
        voice_feedback_id: d.voice_feedback_id || null,
      });
      setD((prev) => ({ ...prev, rubric: { ...editRubric } }));
      setEditing(false);
      toast.success(t("peer_review.toast_review_updated"));
      onUpdated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("peer_review.toast_update_failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-2xl bg-white border border-[#E7E0D2] p-5">
      {/* Header row */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-[0.16em] text-[#4B5563]">
              {d.module_name || d.module_code || "—"}
            </span>
            {d.section && (
              <span className="inline-flex items-center gap-1 text-[10px] font-medium text-[#064E3B] bg-[#ECFDF5] border border-[#A7F3D0] rounded-full px-1.5 py-0.5">
                Section {d.section}
              </span>
            )}
          </div>
          <div className="mt-0.5 flex items-center gap-2 flex-wrap">
            <span className="font-heading text-sm font-semibold text-[#1F2937]">
              #{d.submission_id?.slice(-6)}
            </span>
            {d.submitter_name && (
              <span className="text-xs text-[#4B5563]">by {d.submitter_name}</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {date && <span className="text-[11px] text-[#9CA3AF]">{date}</span>}
          {!editing && (
            <button
              onClick={openEdit}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-[#E7E0D2] text-[11px] text-[#4B5563] hover:border-[#064E3B] hover:text-[#064E3B] transition">
              <Pencil className="w-3 h-3" /> Edit
            </button>
          )}
          {editing && (
            <button
              onClick={() => setEditing(false)}
              className="p-1 rounded-full hover:bg-[#F9F5EE] text-[#4B5563] transition">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {editing ? (
        /* ---- Edit form ---- */
        <div className="mt-4 space-y-4">
          {RUBRIC_KEYS.map((k) => (
            <div key={k}>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-sm font-medium text-[#1F2937]">{RUBRIC_LABELS[k]}</span>
                <span className="text-xs text-[#4B5563]">{editRubric[k] || 0}/5</span>
              </div>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setEditRubric((r) => ({ ...r, [k]: n }))}
                    className={`w-9 h-9 rounded-lg grid place-items-center transition ${
                      n <= (editRubric[k] || 0)
                        ? "bg-[#D97706] text-white"
                        : "bg-white border border-[#E7E0D2] text-[#9CA3AF] hover:border-[#D97706] hover:text-[#D97706]"
                    }`}>
                    <Star className="w-4 h-4" fill={n <= (editRubric[k] || 0) ? "currentColor" : "none"} />
                  </button>
                ))}
              </div>
            </div>
          ))}
          <div>
            <div className="text-xs uppercase tracking-[0.14em] text-[#4B5563] mb-1.5">{t("peer_review.comment_optional")}</div>
            <textarea
              value={editRubric.comment || ""}
              onChange={(e) => setEditRubric((r) => ({ ...r, comment: e.target.value }))}
              rows={3}
              maxLength={1000}
              placeholder={t("peer_review.comment_placeholder")}
              className="w-full px-3 py-2 rounded-lg border border-[#E7E0D2] focus:border-[#064E3B] outline-none text-sm resize-none"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={saveEdit}
              disabled={saving || RUBRIC_KEYS.some((k) => !editRubric[k])}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#064E3B] hover:bg-[#047857] disabled:opacity-40 text-white text-sm font-medium transition">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              {saving ? "Saving…" : "Save changes"}
            </button>
            <button
              onClick={() => setEditing(false)}
              className="px-4 py-2 rounded-full border border-[#E7E0D2] text-[#4B5563] text-sm hover:border-[#064E3B] transition">
              Cancel
            </button>
          </div>
        </div>
      ) : (
        /* ---- Read-only view ---- */
        <>
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {RUBRIC_KEYS.map((k) => (
              <div key={k} className="rounded-xl bg-[#F9F5EE] px-3 py-2 text-center">
                <div className="text-[10px] uppercase tracking-[0.14em] text-[#4B5563]">{RUBRIC_LABELS[k]}</div>
                <div className="flex items-center justify-center gap-0.5 mt-1.5">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star
                      key={n}
                      className={`w-3 h-3 ${n <= (d.rubric?.[k] || 0) ? "text-[#D97706]" : "text-[#E7E0D2]"}`}
                      fill={n <= (d.rubric?.[k] || 0) ? "currentColor" : "none"}
                    />
                  ))}
                </div>
                <div className="text-xs font-semibold text-[#1F2937] mt-0.5">{d.rubric?.[k] || 0}/5</div>
              </div>
            ))}
          </div>

          {d.rubric?.comment && (
            <p className="mt-3 text-sm text-[#4B5563] italic border-l-2 border-[#E7E0D2] pl-3">
              &ldquo;{d.rubric.comment}&rdquo;
            </p>
          )}

          {d.voice_feedback_id && (
            <div className="mt-3 flex items-center gap-2">
              <Mic className="w-3.5 h-3.5 text-[#B45309] shrink-0" />
              <VoicePlayback voiceFeedbackId={d.voice_feedback_id} />
            </div>
          )}
        </>
      )}
    </div>
  );
}

function EmptyQueue({ onRefresh, t, decisions }) {
  return (
    <div data-testid="peer-review-empty">
      {/* Empty-queue notice */}
      <div className="rounded-2xl bg-white border border-[#E7E0D2] p-8 text-center">
        <Sparkles className="w-8 h-8 text-[#D97706] mx-auto" />
        <h3 className="font-heading text-xl text-[#1F2937] mt-3">{t("pr.empty_title")}</h3>
        <p className="text-sm text-[#4B5563] mt-2">{t("pr.empty_body")}</p>
        <button
          onClick={onRefresh}
          className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#064E3B] hover:bg-[#047857] text-white font-medium text-sm transition">
          <RefreshCw className="w-4 h-4" /> {t("pr.refresh")}
        </button>
      </div>

      {/* Review history */}
      {decisions.length > 0 && (
        <div className="mt-8">
          <div className="flex items-center gap-2 mb-4">
            <CheckCircle2 className="w-4 h-4 text-[#064E3B]" />
            <h2 className="font-heading text-lg font-semibold text-[#1F2937]">
              Reviews you submitted
            </h2>
            <span className="ml-auto text-xs text-[#4B5563]">{decisions.length} total</span>
          </div>
          <div className="flex flex-col gap-3">
            {decisions.map((d) => (
              <DecisionCard key={d.decision_id} d={d} onUpdated={onRefresh} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
